import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './ConversionErrorHistory.scss';

const PAGE_SIZE = 50;
const API_URL = process.env.REACT_APP_SERVER_URL || '';
const ACTION_LABELS = { failed: '단계 실패', skipped: '페이지 제외', retrying: '재시도', recovered: '복구 완료' };
const OUTCOME_LABELS = {
    page_succeeded: '본문 변환 성공', recovered: '복구 완료', response_received: '정상 응답 확인',
    page_skipped: '페이지 제외', image_failed: '이미지 미완료',
    unconfirmed: '완료 미확인', unavailable: '결과 조회 실패'
};

function FollowUpResult({ row }) {
    const outcome = row.follow_up || { status: 'unconfirmed', at: '', note: '후속 결과를 확인할 수 없습니다.' };
    const status = OUTCOME_LABELS[outcome.status] ? outcome.status : 'unconfirmed';
    return <div className="error-follow-up" title={outcome.note}>
        <span className={`error-outcome-badge outcome-${status}`}>{OUTCOME_LABELS[status]}</span>
        {outcome.at && <small title={outcome.at}>{outcome.at.slice(11)}</small>}
    </div>;
}
const STAGE_LABELS = {
    prepare: '입력 준비', ai_request: 'AI 요청', page_validation: '응답 검증',
    image_validation: '이미지 좌표 검증', image_analysis: '이미지 최종 결과',
    page_analysis: '페이지 분석', assemble: '결과 정리', backup: '백업 저장',
    billing: '요금 차감', hwp: 'HWP 생성'
};
const CAUSE_LABELS = { quota: '쿼터 초과', capacity: '서버 용량 부족', unknown: '429 원인 미확정' };
const EVIDENCE_LABELS = {
    quota_failure: 'Google 응답의 쿼터 위반 상세', error_info: 'Google 응답의 명시적인 오류 사유',
    response_message: 'Google 오류 문구에 명시됨',
    unspecified: '서버가 쿼터 초과인지 용량 부족인지 알려주지 않았습니다.',
    conflicting: '응답의 원인 정보가 서로 달라 확정할 수 없습니다.'
};

function conversionTaskLabel(row) {
    const serviceName = String(row.service_name || '').trim().toUpperCase();
    if (serviceName.startsWith('PDF2_IMAGE_')
        || row.stage === 'image_validation' || row.stage === 'image_analysis') {
        return '이미지찾기';
    }
    if (serviceName === 'PDF2HWP2' || row.stage === 'hwp') return 'HWP변환';
    return '';
}

function resourceCause(row, diagnostics = {}) {
    if (row.action === 'recovered') return '';
    const cause = diagnostics.resource_exhausted_cause || row.resource_exhausted_cause;
    if (Object.prototype.hasOwnProperty.call(CAUSE_LABELS, cause)) return cause;
    // Old records only support an unknown cause. Never infer overload from 429 alone.
    return /RESOURCE_EXHAUSTED|^\s*429\b|\bHTTP\s+429\b/i.test(row.error_message || '') ? 'unknown' : '';
}

function initialFilters(jobId = '') {
    const today = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const from = new Date(today);
    from.setUTCDate(from.getUTCDate() - 6);
    return {
        keyword: '', jobId, action: '', stage: '', cause: '',
        fromDate: jobId ? '' : from.toISOString().slice(0, 10),
        toDate: jobId ? '' : today.toISOString().slice(0, 10)
    };
}

function ErrorDetails({ row }) {
    const [detail, setDetail] = useState(null);
    const [message, setMessage] = useState('');
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setMessage('');
        setDetail(null);
        axios.post(`${API_URL}/userRoutes/selectConversionErrorDetail`, { eventId: row.event_id }, {
            signal: controller.signal, timeout: 15000
        }).then(({ data }) => {
            if (controller.signal.aborted) return;
            if (!data || data.event_id !== row.event_id) throw new Error('invalid-response');
            setDetail(data);
        }).catch(() => {
            if (!controller.signal.aborted) setMessage('오류 상세를 불러오지 못했습니다. 다시 시도해 주세요.');
        });
        return () => controller.abort();
    }, [row.event_id, retry]);

    let diagnostics = {};
    try {
        const parsed = JSON.parse(detail?.diagnostics_json || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) diagnostics = parsed;
    } catch (error) {
        // Older clients may send no response or memory diagnostics.
    }
    const memoryValue = (key, unit) => {
        const value = diagnostics[key];
        return value !== null && value !== undefined && Number.isFinite(Number(value))
            ? `${Number(value).toLocaleString('ko-KR')}${unit}` : '기록 없음';
    };
    const finishReason = typeof diagnostics.finish_reason === 'string' ? diagnostics.finish_reason : '';
    const finishLabels = {
        STOP: 'STOP (일반 종료 또는 중단 조건)',
        MAX_TOKENS: 'MAX_TOKENS (API 토큰 한도 종료)',
        SAFETY: 'SAFETY (안전 필터)',
        UNKNOWN: '종료 사유를 받지 못함'
    };
    const cause = resourceCause(row, diagnostics);
    const causeEvidence = EVIDENCE_LABELS[diagnostics.resource_exhausted_evidence]
        || '이전 기록에는 상세 분류 근거가 없습니다. 이 기록만으로 원인을 확정할 수 없습니다.';

    return (
        <section className="error-detail-panel" aria-label="오류 상세">
            <h3>오류 메시지</h3>
            <pre className="error-full-message">{detail?.error_message || row.error_message || '기록 없음'}</pre>
            {cause && <p className="error-cause-description"><strong>{CAUSE_LABELS[cause]}</strong> · {detail ? causeEvidence : '분류 근거를 불러오는 중입니다.'}</p>}
            <dl className="error-detail-facts">
                <div><dt>후속 처리 결과</dt><dd><FollowUpResult row={row} /></dd></div>
                <div><dt>작업 ID</dt><dd>{row.job_id || '기록 없음'}</dd></div>
                <div><dt>서비스</dt><dd>{row.service_name || '기록 없음'}</dd></div>
                <div><dt>요청 키</dt><dd>{diagnostics.vertex_setting_title || row.vertex_setting_title || '기록 없음'}</dd></div>
                <div><dt>Google 프로젝트</dt><dd>{diagnostics.vertex_project_id || row.vertex_project_id || '기록 없음'}</dd></div>
                <div><dt>요청 위치</dt><dd>{diagnostics.vertex_location || row.vertex_location || '기록 없음'}</dd></div>
                <div><dt>요청 시도</dt><dd>{row.attempt ? `${row.attempt}회차` : '기록 없음'}</dd></div>
                <div><dt>메모리 사용률</dt><dd>{memoryValue('memory_load_pct', '%')}</dd></div>
                <div><dt>남은 메모리</dt><dd>{memoryValue('available_memory_mb', ' MB')}</dd></div>
                <div><dt>서버 수신 시각</dt><dd>{detail?.received_at || '—'}</dd></div>
                {cause && <>
                    <div><dt>HTTP 상태</dt><dd>{memoryValue('api_http_status', '')}</dd></div>
                    <div><dt>Google 오류 사유</dt><dd>{diagnostics.api_error_reason || '제공되지 않음'}</dd></div>
                    <div><dt>쿼터 항목</dt><dd>{diagnostics.quota_metric || '제공되지 않음'}</dd></div>
                    <div><dt>쿼터 한도 이름</dt><dd>{diagnostics.quota_limit_id || '제공되지 않음'}</dd></div>
                    <div><dt>쿼터 제한값</dt><dd>{diagnostics.quota_limit_value ?? '제공되지 않음'}</dd></div>
                    <div><dt>쿼터 적용 위치</dt><dd>{diagnostics.quota_location || '제공되지 않음'}</dd></div>
                    <div><dt>쿼터 적용 프로젝트</dt><dd>{diagnostics.quota_project || '제공되지 않음'}</dd></div>
                    <div><dt>서버 권장 대기</dt><dd>{diagnostics.api_retry_delay_ms !== null && diagnostics.api_retry_delay_ms !== undefined
                        ? `${Number(diagnostics.api_retry_delay_ms) / 1000}초` : '제공되지 않음'}</dd></div>
                    <div><dt>Google 요청 ID</dt><dd>{diagnostics.api_request_id || '제공되지 않음'}</dd></div>
                </>}
                {diagnostics.image_analysis_status && <>
                    <div><dt>이미지 처리 결과</dt><dd>{{ success: '완료', empty: '정상 · 이미지 없음', failed: '이미지 처리 미완료' }[diagnostics.image_analysis_status] || '확인 필요'}</dd></div>
                    <div><dt>마지막 복구 요청</dt><dd>{diagnostics.image_final_recovery ? '실행함' : '실행 안 함'}</dd></div>
                </>}
                {(diagnostics.image_analysis_status || diagnostics.model_region_count !== undefined) && <>
                    <div><dt>모델이 반환한 영역</dt><dd>{memoryValue('model_region_count', '개')}</dd></div>
                    <div><dt>유효한 좌표</dt><dd>{memoryValue('valid_region_count', '개')}</dd></div>
                    <div><dt>필터 후 영역</dt><dd>{memoryValue('detected_region_count', '개')}</dd></div>
                    <div><dt>준비한 이미지</dt><dd>{memoryValue('prepared_region_count', '개')}</dd></div>
                    <div><dt>저장한 이미지</dt><dd>{memoryValue('stored_region_count', '개')}</dd></div>
                </>}
                {finishReason && <>
                    <div><dt>API 종료 사유</dt><dd>{finishLabels[finishReason] || finishReason}</dd></div>
                    <div><dt>실제 출력 토큰</dt><dd>{memoryValue('output_tokens', ' 토큰')}</dd></div>
                    <div><dt>설정 출력 한도</dt><dd>{memoryValue('configured_output_tokens', ' 토큰')}</dd></div>
                    <div><dt>응답 길이</dt><dd>{memoryValue('response_char_count', '자')}</dd></div>
                    <div><dt>페이지 생성 시도</dt><dd>{memoryValue('page_generation_attempt', '회차')}</dd></div>
                </>}
            </dl>
            {row.follow_up?.note && <p className="error-outcome-note">{row.follow_up.note}</p>}
            {message ? (
                <div role="alert" className="error-load-failure">
                    {message} <button type="button" onClick={() => setRetry(value => value + 1)}>다시 시도</button>
                </div>
            ) : !detail ? <p role="status">상세 기록을 불러오는 중입니다.</p> : (
                <details>
                    <summary>기술 상세 · 호출 위치</summary>
                    <pre>{detail.stack_trace || '호출 위치 기록이 없습니다.'}</pre>
                    <pre>{JSON.stringify(diagnostics, null, 2)}</pre>
                    <p>기록 ID: {row.event_id}</p>
                </details>
            )}
        </section>
    );
}

const ConversionErrorHistory = ({ initialJobId = '' }) => {
    const [filters, setFilters] = useState(() => initialFilters(initialJobId));
    const [query, setQuery] = useState(() => ({ ...initialFilters(initialJobId), page: 1 }));
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        setRows([]);
        setTotalCount(0);
        setExpandedId(null);
        axios.post(`${API_URL}/userRoutes/selectConversionErrorHistory`, { ...query, pageSize: PAGE_SIZE }, {
            signal: controller.signal, timeout: 15000
        }).then(({ data }) => {
            if (controller.signal.aborted) return;
            if (!Array.isArray(data?.rows) || !Number.isFinite(Number(data.totalCount))) throw new Error('invalid-response');
            setRows(data.rows);
            setTotalCount(Number(data.totalCount));
        }).catch(() => {
            if (!controller.signal.aborted) setError('오류 기록을 불러오지 못했습니다. 새로고침하여 다시 시도해 주세요.');
        }).finally(() => {
            if (!controller.signal.aborted) setLoading(false);
        });
        return () => controller.abort();
    }, [query, refresh]);

    const updateFilter = (key, value) => setFilters(current => ({ ...current, [key]: value }));
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const jobColorIndexes = new Map();
    const dateCellClasses = rows.map(row => {
        const jobId = String(row.job_id || '').trim();
        if (!jobId || jobId === 'NULL') return 'error-date';
        if (!jobColorIndexes.has(jobId)) {
            jobColorIndexes.set(jobId, jobColorIndexes.size % 3);
        }
        return `error-date error-date-job-${jobColorIndexes.get(jobId)}`;
    });

    function search(event) {
        event.preventDefault();
        if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
            setFormError('시작일은 종료일보다 늦을 수 없습니다.');
            return;
        }
        setFormError('');
        setQuery({ ...filters, keyword: filters.keyword.trim(), jobId: filters.jobId.trim(), page: 1 });
    }

    function reset() {
        const next = initialFilters();
        setFilters(next);
        setFormError('');
        setQuery({ ...next, page: 1 });
    }

    function showJob(jobId) {
        const next = initialFilters(jobId);
        setFilters(next);
        setFormError('');
        setQuery({ ...next, page: 1 });
    }

    return (
        <section className="conversion-error-history" aria-labelledby="conversion-error-title">
            <header className="conversion-error-header">
                <h2 id="conversion-error-title">변환 오류 기록</h2>
                <p>재시도·단계 실패 기록은 작업 전체의 최종 실패를 뜻하지 않습니다. 시각은 한국 시간입니다.</p>
                <p>후속 결과는 해당 페이지·단계 기준입니다. 정상 응답 확인만으로 최종 성공을 뜻하지 않으며, 본문 변환 성공과 HWP 파일 생성 완료는 별개입니다.</p>
                <p>429는 서버 응답의 근거로 구분합니다. 근거가 없으면 원인 미확정으로 표시합니다.</p>
            </header>
            <form className="conversion-error-filters" onSubmit={search}>
                <label>시작일<input type="date" value={filters.fromDate} onChange={event => updateFilter('fromDate', event.target.value)} /></label>
                <label>종료일<input type="date" value={filters.toDate} onChange={event => updateFilter('toDate', event.target.value)} /></label>
                <label className="error-keyword-field">사용자·오류 검색
                    <input value={filters.keyword} maxLength={200} placeholder="이름, 아이디, 오류 내용" onChange={event => updateFilter('keyword', event.target.value)} />
                </label>
                <label className="error-job-field">작업 ID
                    <input value={filters.jobId} maxLength={80} placeholder="작업 ID 전체 입력" onChange={event => updateFilter('jobId', event.target.value)} />
                </label>
                <label>처리 구분<select value={filters.action} onChange={event => updateFilter('action', event.target.value)}>
                    <option value="">전체</option>
                    {Object.entries(ACTION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select></label>
                <label>단계<select value={filters.stage} onChange={event => updateFilter('stage', event.target.value)}>
                    <option value="">전체 단계</option>
                    {Object.entries(STAGE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select></label>
                <label>429 원인<select value={filters.cause} onChange={event => updateFilter('cause', event.target.value)}>
                    <option value="">전체</option>
                    {Object.entries(CAUSE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select></label>
                <div className="conversion-error-actions">
                    <button type="submit" className="error-search-button">조회</button>
                    <button type="button" onClick={reset}>초기화</button>
                    <button type="button" onClick={() => setRefresh(value => value + 1)} disabled={loading}>새로고침</button>
                </div>
            </form>
            {formError && <p role="alert" className="error-load-failure">{formError}</p>}
            {error && <p role="alert" className="error-load-failure">{error}</p>}
            <div className="conversion-error-table-wrap" aria-busy={loading}>
                <table className="conversion-error-table">
                    <caption className="error-visually-hidden">변환 오류 기록 목록</caption>
                    <thead><tr>
                        <th scope="col" className="error-no">no</th>
                        <th scope="col">발생 시각</th><th scope="col">사용자</th>
                        <th scope="col" className="error-app-version">appVer</th><th scope="col">처리 구분</th>
                        <th scope="col">후속 결과</th>
                        <th scope="col">단계</th><th scope="col">페이지</th><th scope="col">429 원인</th><th scope="col">오류 내용</th>
                        <th scope="col">모델</th><th scope="col">요청 키</th><th scope="col">작업 ID</th><th scope="col">상세</th>
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan={14} className="error-empty" role="status">오류 기록을 조회하고 있습니다.</td></tr>
                            : error ? <tr><td colSpan={14} className="error-empty">조회에 실패했습니다. 새로고침해 주세요.</td></tr>
                                : rows.length === 0 ? <tr><td colSpan={14} className="error-empty">
                                    <strong>조회 조건에 맞는 오류 기록이 없습니다.</strong>
                                    <span>오류 기록 기능이 적용된 앱에서 발생한 오류부터 표시됩니다.</span>
                                </td></tr> : rows.map((row, index) => (
                                    <React.Fragment key={row.event_id}>
                                        <tr className={expandedId === row.event_id ? 'error-row-expanded' : ''}>
                                            <td className={`error-no ${dateCellClasses[index]}`}>{(query.page - 1) * PAGE_SIZE + index + 1}</td>
                                            <td className={dateCellClasses[index]}>{row.occurred_at}</td>
                                            <td><strong>{row.user_name || row.user_id || '알 수 없음'}</strong><small>{row.user_id}</small></td>
                                            <td className="error-app-version">{row.app_version || '—'}</td>
                                            <td><span className={`error-action-badge action-${ACTION_LABELS[row.action] ? row.action : 'unknown'}`}>{ACTION_LABELS[row.action] || row.action || '—'}</span></td>
                                            <td><FollowUpResult row={row} /></td>
                                            <td>{STAGE_LABELS[row.stage] || row.stage}</td>
                                            <td className="error-page">{row.page_number ?? '—'}</td>
                                            <td>{resourceCause(row) ? <span className={`error-cause-badge cause-${resourceCause(row)}`}>{CAUSE_LABELS[resourceCause(row)]}</span> : '—'}</td>
                                            <td className="error-message-cell"><strong>{row.error_type}</strong><span title={row.error_message}>{row.error_message}</span></td>
                                            <td className="error-model">
                                                <span>{row.model_name || '—'}</span>
                                                {conversionTaskLabel(row) && <small className={`error-model-task ${conversionTaskLabel(row) === '이미지찾기' ? 'error-model-task-image' : ''}`}>
                                                    {conversionTaskLabel(row)}
                                                </small>}
                                            </td>
                                            <td className="error-request-key" title={row.vertex_project_id || undefined}>{row.vertex_setting_title || '기록 없음'}</td>
                                            <td className="error-job-id">{row.job_id ? <button type="button" title="이 작업의 모든 오류 보기" onClick={() => showJob(row.job_id)}>{row.job_id}</button> : '—'}</td>
                                            <td><button type="button" className="error-detail-toggle" aria-expanded={expandedId === row.event_id} aria-controls={`error-detail-${row.event_id}`} onClick={() => setExpandedId(current => current === row.event_id ? null : row.event_id)}>
                                                {expandedId === row.event_id ? '닫기' : '상세'}
                                            </button></td>
                                        </tr>
                                        {expandedId === row.event_id && <tr id={`error-detail-${row.event_id}`}><td colSpan={14}><ErrorDetails row={row} /></td></tr>}
                                    </React.Fragment>
                                ))}
                    </tbody>
                </table>
            </div>
            <footer className="conversion-error-pagination">
                <span aria-live="polite">{loading ? '조회 중…' : error ? '조회 실패' : `기록 ${totalCount.toLocaleString('ko-KR')}건`}</span>
                <div>
                    <button type="button" disabled={loading || Boolean(error) || query.page <= 1} onClick={() => setQuery(current => ({ ...current, page: current.page - 1 }))}>이전</button>
                    <span>{query.page} / {totalPages}</span>
                    <button type="button" disabled={loading || Boolean(error) || query.page >= totalPages} onClick={() => setQuery(current => ({ ...current, page: current.page + 1 }))}>다음</button>
                </div>
            </footer>
        </section>
    );
};

export default ConversionErrorHistory;
